import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag, TrendingUp, CircleCheck as CheckCircle, Circle as XCircle, Package, TriangleAlert as AlertTriangle, RefreshCw, IndianRupee, Clock, ChefHat, Plus } from 'lucide-react';
import { KITCHEN_DASHBOARD_URL, INVENTORY_SUMMARY_URL, INVENTORY_UPDATE_URL, ORDER_STATUS_UPDATE_URL } from '../../config/api';
import { authenticatedFetch, FOOD_FALLBACK_IMG } from '../Utils/apiHelpers';
import './KitchenDashboardTab.css';

interface DashboardAnalytics {
  total_orders: number;
  pending_orders: number;
  completed_orders: number;
  cancelled_orders: number;
  total_revenue: number;
  today_revenue: number;
  monthly_revenue: number;
  total_food_items: number;
}

interface TopFood {
  food_id: number;
  food_name: string;
  image: string;
  price: number;
  sold_quantity: number;
}

interface LowStockFood {
  food_id: number;
  food_name: string;
  image: string;
  remaining_quantity: number;
  is_available: boolean;
}

interface RecentOrderItem {
  food_id: number;
  food_name: string;
  food_image: string;
  quantity: number;
  price: number;
  subtotal: number;
}

interface RecentOrder {
  order_id: number;
  order_status: string;
  payment_status: string;
  total_price: number;
  created_at: string;
  items: RecentOrderItem[];
}

interface DashboardData {
  success: boolean;
  kitchen: { kitchen_id: number; kitchen_name: string; status: boolean; is_approved: boolean };
  analytics: DashboardAnalytics;
  top_selling_foods: TopFood[];
  low_stock_foods: LowStockFood[];
  recent_orders: RecentOrder[];
}

interface KitchenDashboardTabProps {
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  isKitchenBlocked?: boolean;
}

interface InventorySummary {
  total_products: number;
  available_products: number;
  low_stock_products: number;
  sold_out_products: number;
}

const STATUS_COLOR: Record<string, string> = {
  pending:         '#ffc107',
  confirmed:       '#17a2b8',
  accepted:        '#20c997',
  preparing:       '#fd7e14',
  ready:           '#fd7e14',
  picked_up:       '#6f42c1',
  delivered:       '#28a745',
  completed:       '#28a745',
  cancelled:       '#6c757d',
  rejected:        '#dc3545',
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

const KitchenDashboardTab: React.FC<KitchenDashboardTabProps> = ({ showNotification, isKitchenBlocked = false }) => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [invSummary, setInvSummary] = useState<InventorySummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restockingId, setRestockingId] = useState<number | null>(null);
  const [restockQty, setRestockQty] = useState<Record<number, string>>({});
  const [orderActionId, setOrderActionId] = useState<number | null>(null);

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [dashRes, invRes] = await Promise.all([
        authenticatedFetch(KITCHEN_DASHBOARD_URL, { method: 'GET', headers: { accept: 'application/json' } }),
        authenticatedFetch(INVENTORY_SUMMARY_URL, { method: 'GET', headers: { accept: 'application/json' } }),
      ]);
      if (dashRes.ok) {
        const json: DashboardData = await dashRes.json();
        setData(json);
      } else if (dashRes.status === 404) {
        setError('Dashboard not available. Create a kitchen first.');
      } else {
        setError('Failed to load dashboard. Please try again.');
      }
      if (invRes.ok) {
        const invJson = await invRes.json();
        if (invJson.success) setInvSummary(invJson);
      }
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleQuickRestock = async (foodId: number) => {
    const qty = parseInt(restockQty[foodId] || '0', 10);
    if (!qty || qty < 1) {
      showNotification('error', 'Invalid Quantity', 'Enter a quantity of at least 1.');
      return;
    }
    setRestockingId(foodId);
    try {
      const res = await authenticatedFetch(INVENTORY_UPDATE_URL(foodId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ available_quantity: qty }),
      });
      if (res.ok) {
        showNotification('success', 'Restocked', `Quantity updated to ${qty}.`);
        setRestockQty(prev => { const n = { ...prev }; delete n[foodId]; return n; });
        fetchDashboard();
      } else {
        showNotification('error', 'Restock Failed', 'Could not update inventory. Please try again.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection.');
    } finally {
      setRestockingId(null);
    }
  };

  const handleQuickOrderAction = async (orderId: number, status: 'accepted' | 'rejected') => {
    setOrderActionId(orderId);
    try {
      const res = await authenticatedFetch(`${ORDER_STATUS_UPDATE_URL(orderId)}?status=${status}`, {
        method: 'POST',
        headers: { accept: 'application/json' },
      });
      if (res.ok) {
        showNotification('success', 'Order Updated', `Order #${orderId} ${status}.`);
        fetchDashboard();
      } else {
        showNotification('error', 'Update Failed', 'Could not update order status.');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection.');
    } finally {
      setOrderActionId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="user-profile-empty-state">
        <div className="user-profile-loading-spinner" style={{ width: '40px', height: '40px', marginBottom: '1rem' }} />
        <p>Loading kitchen dashboard...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="user-profile-empty-state">
        <AlertTriangle size={52} />
        <p>{error ?? 'No data available.'}</p>
        <button className="user-profile-edit-btn" onClick={fetchDashboard} style={{ marginTop: '1rem' }}>
          <RefreshCw size={16} /> Try Again
        </button>
      </div>
    );
  }

  const { analytics, top_selling_foods, low_stock_foods, recent_orders } = data;

  return (
    <div className="kdash-container">
      {/* Header */}
      <div className="kdash-header">
        <div>
          <h4>Kitchen Dashboard</h4>
          <p>Analytics and overview for {data.kitchen.kitchen_name}</p>
        </div>
        <button className="user-profile-edit-btn" onClick={fetchDashboard}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Analytics grid */}
      <div className="kdash-analytics-grid">
        <div className="kdash-stat-card">
          <div className="kdash-stat-icon" style={{ background: 'rgba(23, 162, 184, 0.1)', color: '#17a2b8' }}>
            <ShoppingBag size={20} />
          </div>
          <div className="kdash-stat-body">
            <span className="kdash-stat-value">{analytics.total_orders}</span>
            <span className="kdash-stat-label">Total Orders</span>
          </div>
        </div>

        <div className="kdash-stat-card">
          <div className="kdash-stat-icon" style={{ background: 'rgba(255, 193, 7, 0.1)', color: '#ffc107' }}>
            <Clock size={20} />
          </div>
          <div className="kdash-stat-body">
            <span className="kdash-stat-value">{analytics.pending_orders}</span>
            <span className="kdash-stat-label">Pending</span>
          </div>
        </div>

        <div className="kdash-stat-card">
          <div className="kdash-stat-icon" style={{ background: 'rgba(40, 167, 69, 0.1)', color: '#28a745' }}>
            <CheckCircle size={20} />
          </div>
          <div className="kdash-stat-body">
            <span className="kdash-stat-value">{analytics.completed_orders}</span>
            <span className="kdash-stat-label">Completed</span>
          </div>
        </div>

        <div className="kdash-stat-card">
          <div className="kdash-stat-icon" style={{ background: 'rgba(220, 53, 69, 0.1)', color: '#dc3545' }}>
            <XCircle size={20} />
          </div>
          <div className="kdash-stat-body">
            <span className="kdash-stat-value">{analytics.cancelled_orders}</span>
            <span className="kdash-stat-label">Cancelled</span>
          </div>
        </div>

        <div className="kdash-stat-card">
          <div className="kdash-stat-icon" style={{ background: 'rgba(255, 107, 53, 0.1)', color: '#ff6b35' }}>
            <IndianRupee size={20} />
          </div>
          <div className="kdash-stat-body">
            <span className="kdash-stat-value">£{analytics.today_revenue.toFixed(0)}</span>
            <span className="kdash-stat-label">Today's Revenue</span>
          </div>
        </div>

        <div className="kdash-stat-card">
          <div className="kdash-stat-icon" style={{ background: 'rgba(111, 66, 193, 0.1)', color: '#6f42c1' }}>
            <TrendingUp size={20} />
          </div>
          <div className="kdash-stat-body">
            <span className="kdash-stat-value">£{analytics.monthly_revenue.toFixed(0)}</span>
            <span className="kdash-stat-label">Monthly Revenue</span>
          </div>
        </div>

        <div className="kdash-stat-card">
          <div className="kdash-stat-icon" style={{ background: 'rgba(32, 201, 151, 0.1)', color: '#20c997' }}>
            <IndianRupee size={20} />
          </div>
          <div className="kdash-stat-body">
            <span className="kdash-stat-value">£{analytics.total_revenue.toFixed(0)}</span>
            <span className="kdash-stat-label">Total Revenue</span>
          </div>
        </div>

        <div className="kdash-stat-card">
          <div className="kdash-stat-icon" style={{ background: 'rgba(0, 123, 255, 0.1)', color: '#007bff' }}>
            <Package size={20} />
          </div>
          <div className="kdash-stat-body">
            <span className="kdash-stat-value">{analytics.total_food_items}</span>
            <span className="kdash-stat-label">Food Items</span>
          </div>
        </div>
      </div>

      {/* Inventory Health Row */}
      {invSummary && (
        <div className="kdash-inv-row">
          <span className="kdash-inv-label">Inventory Health:</span>
          <span className="kdash-inv-chip kdash-inv-chip--total">{invSummary.total_products} total</span>
          <span className="kdash-inv-chip kdash-inv-chip--ok">{invSummary.available_products} available</span>
          {invSummary.low_stock_products > 0 && (
            <span className="kdash-inv-chip kdash-inv-chip--warn">{invSummary.low_stock_products} low stock</span>
          )}
          {invSummary.sold_out_products > 0 && (
            <span className="kdash-inv-chip kdash-inv-chip--danger">{invSummary.sold_out_products} sold out</span>
          )}
        </div>
      )}

      <div className="kdash-lower-grid">
        {/* Top Selling Foods */}
        {top_selling_foods.length > 0 && (
          <div className="kdash-card">
            <h5 className="kdash-card-title">
              <TrendingUp size={16} />
              Top Selling Foods
            </h5>
            <ul className="kdash-food-list">
              {top_selling_foods.map(food => (
                <li key={food.food_id} className="kdash-food-item">
                  <img
                    src={food.image && food.image !== 'string' ? food.image : FOOD_FALLBACK_IMG}
                    alt={food.food_name}
                    className="kdash-food-img"
                    onError={(e) => { (e.target as HTMLImageElement).src = FOOD_FALLBACK_IMG; }}
                  />
                  <div className="kdash-food-info">
                    <span className="kdash-food-name">{food.food_name}</span>
                    <span className="kdash-food-meta">£{food.price} · {food.sold_quantity} sold</span>
                  </div>
                  <span className="kdash-sold-badge">{food.sold_quantity}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Low Stock Foods */}
        {low_stock_foods.length > 0 && (
          <div className="kdash-card">
            <h5 className="kdash-card-title kdash-card-title--warn">
              <AlertTriangle size={16} />
              Low Stock Alerts
            </h5>
            <ul className="kdash-food-list">
              {low_stock_foods.map(food => (
                <li key={food.food_id} className="kdash-food-item kdash-food-item--restock">
                  <img
                    src={food.image && food.image !== 'string' ? food.image : FOOD_FALLBACK_IMG}
                    alt={food.food_name}
                    className="kdash-food-img"
                    onError={(e) => { (e.target as HTMLImageElement).src = FOOD_FALLBACK_IMG; }}
                  />
                  <div className="kdash-food-info">
                    <span className="kdash-food-name">{food.food_name}</span>
                    <span className="kdash-food-meta">{food.remaining_quantity} remaining</span>
                  </div>
                  <div className="kdash-restock-row">
                    <input
                      type="number"
                      min={1}
                      className="kdash-restock-input"
                      placeholder="Qty"
                      value={restockQty[food.food_id] || ''}
                      onChange={e => setRestockQty(prev => ({ ...prev, [food.food_id]: e.target.value }))}
                      disabled={restockingId === food.food_id}
                    />
                    <button
                      className="kdash-restock-btn"
                      onClick={() => handleQuickRestock(food.food_id)}
                      disabled={isKitchenBlocked || restockingId === food.food_id}
                      title="Update stock"
                    >
                      {restockingId === food.food_id ? '...' : <Plus size={14} />}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recent Orders */}
        {recent_orders.length > 0 && (
          <div className="kdash-card kdash-card--full">
            <h5 className="kdash-card-title">
              <ShoppingBag size={16} />
              Recent Orders
            </h5>
            <div className="kdash-orders-list">
              {recent_orders.map(order => {
                const statusColor = STATUS_COLOR[(order.order_status || '').toLowerCase()] ?? '#6c757d';
                return (
                  <div key={order.order_id} className="kdash-order-row">
                    <div className="kdash-order-left">
                      <span className="kdash-order-id">#{order.order_id}</span>
                      <div
                        className="kdash-order-status"
                        style={{ background: `${statusColor}20`, color: statusColor }}
                      >
                        {order.order_status}
                      </div>
                    </div>
                    <div className="kdash-order-center">
                      {order.items.map(item => (
                        <span key={item.food_id} className="kdash-order-item">
                          {item.food_name} ×{item.quantity}
                        </span>
                      ))}
                    </div>
                    <div className="kdash-order-right">
                      <span className="kdash-order-price">£{order.total_price}</span>
                      <span className="kdash-order-date">{formatDate(order.created_at)}</span>
                      {order.order_status.toLowerCase() === 'confirmed' && (
                        <div className="kdash-order-actions">
                          <button
                            className="kdash-order-action-btn kdash-order-action-btn--accept"
                            onClick={() => handleQuickOrderAction(order.order_id, 'accepted')}
                            disabled={isKitchenBlocked || orderActionId === order.order_id}
                          >
                            {orderActionId === order.order_id ? '...' : 'Accept'}
                          </button>
                          <button
                            className="kdash-order-action-btn kdash-order-action-btn--reject"
                            onClick={() => handleQuickOrderAction(order.order_id, 'rejected')}
                            disabled={isKitchenBlocked || orderActionId === order.order_id}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {top_selling_foods.length === 0 && low_stock_foods.length === 0 && recent_orders.length === 0 && (
          <div className="kdash-card kdash-card--full" style={{ textAlign: 'center', padding: '2rem' }}>
            <ChefHat size={48} style={{ color: '#ccc', marginBottom: '0.75rem' }} />
            <p style={{ color: '#888', margin: 0 }}>No orders yet. Start accepting orders to see analytics here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KitchenDashboardTab;
